<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

// Lista de todas as permissões disponíveis
const availablePermissions = [
  { key: 'inbox_manage', label: 'Gerenciar Caixas de Entrada', icon: 'i-lucide-inbox' },
  { key: 'team_manage', label: 'Gerenciar Times', icon: 'i-lucide-users' },
  { key: 'automation_manage', label: 'Gerenciar Automações', icon: 'i-lucide-zap' },
  { key: 'campaign_manage', label: 'Gerenciar Campanhas', icon: 'i-lucide-megaphone' },
  { key: 'agent_bot_manage', label: 'Gerenciar Bots', icon: 'i-lucide-bot' },
  { key: 'knowledge_base_manage', label: 'Gerenciar Base de Conhecimento', icon: 'i-lucide-book-open' },
  { key: 'contact_manage', label: 'Gerenciar Contatos', icon: 'i-lucide-contact' },
  { key: 'label_manage', label: 'Gerenciar Etiquetas', icon: 'i-lucide-tag' },
  { key: 'webhook_manage', label: 'Gerenciar Webhooks', icon: 'i-lucide-webhook' },
  { key: 'canned_response_manage', label: 'Gerenciar Respostas Prontas', icon: 'i-lucide-message-square-text' },
  { key: 'macro_manage', label: 'Criar Macros Globais', icon: 'i-lucide-play-circle' },
  { key: 'report_manage', label: 'Ver Relatórios', icon: 'i-lucide-bar-chart-3' },
  { key: 'agent_manage', label: 'Gerenciar Agentes', icon: 'i-lucide-user-cog' },
  { key: 'settings_manage', label: 'Gerenciar Configurações', icon: 'i-lucide-settings' },
];

// Estado local das permissões
const localPermissions = ref({});

// Inicializar com os valores das props
onMounted(() => {
  localPermissions.value = { ...props.modelValue };
});

// Watch para atualizar quando props mudam
watch(
  () => props.modelValue,
  newVal => {
    localPermissions.value = { ...newVal };
  },
  { deep: true }
);

// Permissões selecionadas (para mostrar as tags)
const selectedPermissions = computed(() => {
  return availablePermissions.filter(perm => localPermissions.value[perm.key]);
});

// Contador de selecionados
const selectedCount = computed(() => selectedPermissions.value.length);

// Toggle de permissão individual
const togglePermission = key => {
  if (props.disabled) return;
  
  localPermissions.value = {
    ...localPermissions.value,
    [key]: !localPermissions.value[key],
  };
  emit('update:modelValue', localPermissions.value);
};

// Remover permissão (quando clica no X da tag)
const removePermission = key => {
  if (props.disabled) return;
  
  localPermissions.value = {
    ...localPermissions.value,
    [key]: false,
  };
  emit('update:modelValue', localPermissions.value);
};

// Selecionar todas
const selectAll = () => {
  if (props.disabled) return;
  
  const allSelected = {};
  availablePermissions.forEach(perm => {
    allSelected[perm.key] = true;
  });
  localPermissions.value = allSelected;
  emit('update:modelValue', localPermissions.value);
};

// Desselecionar todas
const deselectAll = () => {
  if (props.disabled) return;
  
  const allDeselected = {};
  availablePermissions.forEach(perm => {
    allDeselected[perm.key] = false;
  });
  localPermissions.value = allDeselected;
  emit('update:modelValue', localPermissions.value);
};

// Verificar se todas estão selecionadas
const allSelected = computed(() => {
  return availablePermissions.every(perm => localPermissions.value[perm.key]);
});
</script>

<template>
  <div class="permission-selector">
    <!-- Header com Select All -->
    <div class="flex items-center justify-between mb-3 pb-2 border-b border-slate-100 dark:border-slate-700">
      <label class="flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          :checked="allSelected"
          :disabled="disabled"
          class="w-4 h-4 rounded border-slate-300 text-woot-500 focus:ring-woot-500 dark:border-slate-600 dark:bg-slate-700"
          @change="allSelected ? deselectAll() : selectAll()"
        />
        <span class="text-sm font-medium text-slate-700 dark:text-slate-200">
          Selecionar todas
        </span>
      </label>
      <span class="text-xs text-slate-500 dark:text-slate-400">
        {{ selectedCount }} de {{ availablePermissions.length }} selecionadas
      </span>
    </div>

    <!-- Lista de Checkboxes -->
    <div class="permissions-list max-h-48 overflow-y-auto mb-4 pr-1">
      <label
        v-for="permission in availablePermissions"
        :key="permission.key"
        class="flex items-center gap-3 py-2 px-2 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
        :class="{ 'opacity-50 cursor-not-allowed': disabled }"
      >
        <input
          type="checkbox"
          :checked="localPermissions[permission.key]"
          :disabled="disabled"
          class="w-4 h-4 rounded border-slate-300 text-woot-500 focus:ring-woot-500 dark:border-slate-600 dark:bg-slate-700"
          @change="togglePermission(permission.key)"
        />
        <span class="text-sm text-slate-700 dark:text-slate-200">
          {{ permission.label }}
        </span>
      </label>
    </div>

    <!-- Caixa de Tags Selecionadas -->
    <div
      v-if="selectedPermissions.length > 0"
      class="selected-tags-box min-h-[60px] p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-600"
    >
      <div class="flex flex-wrap gap-2">
        <span
          v-for="permission in selectedPermissions"
          :key="permission.key"
          class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-woot-100 text-woot-700 dark:bg-woot-900/30 dark:text-woot-300 rounded-full"
        >
          {{ permission.label }}
          <button
            v-if="!disabled"
            type="button"
            class="ml-1 text-woot-500 hover:text-woot-700 dark:hover:text-woot-200 focus:outline-none"
            @click.stop="removePermission(permission.key)"
          >
            <span class="i-lucide-x w-3 h-3" />
          </button>
        </span>
      </div>
    </div>
    
    <div
      v-else
      class="selected-tags-box min-h-[60px] p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-dashed border-slate-300 dark:border-slate-600 flex items-center justify-center"
    >
      <span class="text-sm text-slate-400 dark:text-slate-500">
        Nenhuma permissão selecionada
      </span>
    </div>
  </div>
</template>

<style scoped>
.permissions-list::-webkit-scrollbar {
  width: 6px;
}

.permissions-list::-webkit-scrollbar-track {
  background: transparent;
}

.permissions-list::-webkit-scrollbar-thumb {
  background-color: rgba(100, 116, 139, 0.3);
  border-radius: 3px;
}

.permissions-list::-webkit-scrollbar-thumb:hover {
  background-color: rgba(100, 116, 139, 0.5);
}
</style>
